/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { CMSHeadline } from '@/features/contentstack/components/CMSHeadline/CMSHeadline';
import { type FormattedContentStackProductCard } from '@/features/contentstack/interface/models';
import { View, Text, Button } from '@az/starc-ui';
import CMSProductCardStyles from './CMSProductCard.module.scss';
import NextImage from '@/components/NextImage';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Fragment, useEffect, useRef } from 'react';
import { CMSBlogRichText } from '../CMSBlogRichText/CMSBlogRichText';

type CMSProductCardProps = {
  content: FormattedContentStackProductCard;
};

export const CMSProductCard = ({ content }: CMSProductCardProps) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!descriptionRef.current) {
      return;
    }

    const strongElements = descriptionRef.current.querySelectorAll('strong');
    const prosAndConsElements = Array.from(strongElements).filter(
      (element) =>
        element.textContent?.toLowerCase().includes('pros') ||
        element.textContent?.toLowerCase().includes('cons'),
    );

    prosAndConsElements.forEach((element) => {
      element.classList.add(CMSProductCardStyles.prosAndConsTitle);
    });
  }, [descriptionRef]);

  return (
    <View direction="column" maxWidth={{ s: '1107px', l: '830px' }} width="100%">
      {!!content?.title && (
        <CMSHeadline as="h2" underline={!!content.title_underline}>
          {content.title}
        </CMSHeadline>
      )}
      <View
        direction={{ s: 'column-reverse', m: 'row' }}
        className={CMSProductCardStyles.container}
      >
        <View className={CMSProductCardStyles.description}>
          <CMSBlogRichText content={content?.description} textRef={descriptionRef} />
        </View>
        <View
          direction="row"
          justify={{ s: 'start', m: 'center' }}
          className={CMSProductCardStyles.productsContainer}
        >
          {content?.product?.map((product) => {
            return (
              <Fragment key={product.product_name}>
                {isMobile && (
                  <Text weight="bold" className={CMSProductCardStyles.name}>
                    {product.product_name}
                  </Text>
                )}
                <View
                  direction={{ s: 'row', m: 'column' }}
                  align="center"
                  className={CMSProductCardStyles.product}
                >
                  <NextImage
                    className={CMSProductCardStyles.image}
                    width={isMobile ? 98 : 174}
                    height={isMobile ? 98 : 174}
                    src={product.product_image ?? ''}
                    alt={product.product_image_alt_text ?? ''}
                  />
                  {!isMobile && (
                    <Text weight="bold" className={CMSProductCardStyles.name}>
                      {product.product_name}
                    </Text>
                  )}
                  {!!product.button_text && !!product.product_link && (
                    <View padding={[4, 0, 0, 0]} width={{ s: 'auto', m: '100%' }}>
                      <Button
                        className={CMSProductCardStyles.button}
                        variant={isMobile ? 'pill' : undefined}
                        href={product.product_link}
                      >
                        <Text className={CMSProductCardStyles.buttonText}>
                          {product.button_text}
                        </Text>
                      </Button>
                    </View>
                  )}
                </View>
              </Fragment>
            );
          })}
        </View>
      </View>
    </View>
  );
};
